import React, { useRef, useState } from 'react';
import { IonCard, IonCardContent, IonButton, IonIcon, IonChip, IonLabel, IonPopover, IonContent } from '@ionic/react';
import { eye, eyeOff, chevronDownOutline } from 'ionicons/icons';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/services/redux/hook';
import { useUpdateUserDefaultCurrencyMutation, useGetQuotesQuery } from '@/services/api/ebioro/ebioro';
import { useAccountDetailsQuery } from '@/services/api/stellar';
import { setUser } from '@/services/redux/slices/user/user';
import { formatConversion } from '@/services/utils/formatConversion';
import supportedCurrencies from '@/currencies.json';
import { useBalanceVisibility } from '@/hooks/useBalanceVisibility';

interface BalanceCardProps {
  balance?: number;
  style?: React.CSSProperties;
}

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';

export default function BalanceCard({ balance = 0, style }: BalanceCardProps) {
  const userInfo = useAppSelector((state) => state.user);
  const walletInfo = useAppSelector((state) => state.walletInfo);
  const popover = useRef<HTMLIonPopoverElement>(null);
  const dispatch = useAppDispatch();
  const [updateCurrency] = useUpdateUserDefaultCurrencyMutation();
  const { t } = useTranslation('components/balanceCard');
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Use the balance visibility hook
  const { isBalanceHidden, toggleBalanceVisibility } = useBalanceVisibility();

  const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });

  // Get Redux state
  const supportedAssets = useAppSelector((state) => state.supportedAssets.supportedAssets);
  const currentCurrency = userInfo.currency_code || 'USD';
  const balances = accountDetails?.details?.balances;

  // Get auth token for quotes API
  const ebioroAuthToken = useAppSelector((state) => state.sep10AuthToken.tokens[ebioroAuthDomain]);

  // Build coins string for quotes API
  let coins = '';
  if (supportedAssets) {
    supportedAssets.map((asset: any) => {
      if (!coins || coins === '') {
        coins = asset.code;
      } else {
        coins = coins + ',' + asset.code;
      }
    });
  }

  // Get quotes using the same pattern as AccountsPage
  const { data: quotes = [], isSuccess: quotesIsSuccess } = useGetQuotesQuery(
    { coins: coins, authToken: ebioroAuthToken },
    { skip: ebioroAuthToken === '' || ebioroAuthToken === undefined, refetchOnMountOrArgChange: true }
  );

  const handleCurrencyChange = async (currencyCode: string) => {
    if (userInfo.id && currencyCode !== currentCurrency) {
      try {
        const updatedUser = await updateCurrency({ 
          id: userInfo.id, 
          currency_code: currencyCode 
        }).unwrap();
        
        // Update Redux state with the new user info
        dispatch(setUser(updatedUser));
      } catch (error) {
        // Error handling for currency update
      }
    }
  };

   const openPopover = (e: any) => {
    popover.current!.event = e;
    setPopoverOpen(true);
  };

  // Calculate global balance using the same logic as AccountsPage
  const calculateGlobalBalance = React.useMemo((): number => {
 
    if (!quotesIsSuccess || !userInfo || !balances || !supportedAssets) {
      return balance; // fallback to prop balance
    }

    let totalBalance = 0;

    supportedAssets.forEach((asset: any) => {
      // Check if this asset has a balance
      const assetBalance = balances[asset.identifier];
      if (assetBalance && userInfo) {
        // Look for quotes matching this asset code
        const quoteData = quotes[asset.code];
        if (quoteData) {
          const quoteValue = quoteData[userInfo.currency_code];
          if (quoteValue) {
            const amountToAdd = quoteValue * assetBalance.available.toNumber();
            totalBalance += amountToAdd;
          }
        }
      }
    });
    return totalBalance;
  }, [quotesIsSuccess, quotes, userInfo, balances, supportedAssets, balance]);

  const formatBalance = (amount: number): string => {
    return formatConversion(amount).toString();
  };

  const getCurrencySymbol = (code: string): string => {
    const currency = supportedCurrencies.find(curr => curr.code === code);
    return currency?.symbol || '$';
  };

  // Dynamic font size based on balance length
  const getDynamicFontSize = (balanceText: string): string => {
    const length = balanceText.length;
    if (length <= 8) return '2rem';        // Normal size for short amounts
    if (length <= 12) return '1.75rem';    // Slightly smaller for medium amounts  
    if (length <= 16) return '1.5rem';     // Smaller for long amounts
    return '1.25rem';                      // Smallest for very long amounts
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <IonCard 
        className="relative overflow-hidden rounded-3xl shadow-2xl border-0 m-0"
        style={{
          background: 'linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 50%, var(--ion-color-primary-tint) 100%)',
          '--background': 'transparent',
          ...style
        }}
      >
        <IonCardContent className="p-6 relative">
          {/* Decorative logo */}
          <img 
            src="/assets/img/ebioro-symbol-white.svg" 
            alt="Ebioro Logo" 
            className="absolute -right-12 -top-12 h-56 w-56 opacity-15 z-0"
          />
          
          <div className="relative z-10">
            <div className="flex flex-col mb-6">
              <div className="flex flex-col items-start">
                <p className="text-sm font-medium mb-3 text-left" style={{ color: 'white' }}>
                  {t('networth')}
                </p>
                
                {/* Balance and Eye Button on Same Line */}
                <div className="flex items-center justify-between w-full" style={{ margin: '24px 0' }}>
                  <div className="flex items-center gap-3">
                    {isBalanceHidden ? (
                      <span 
                        className="text-left" 
                        style={{ 
                          fontSize: '2rem', 
                          fontWeight: '900', 
                          lineHeight: '1.1',
                          color: 'white'
                        }}
                      >
                        ••••••
                      </span>
                    ) : (
                      <span 
                        className="text-left" 
                        style={{ 
                          fontSize: getDynamicFontSize(`${getCurrencySymbol(currentCurrency)}${formatBalance(calculateGlobalBalance)}`), 
                          fontWeight: '900', 
                          lineHeight: '1.1',
                          color: 'white'
                        }}
                      >
                        {getCurrencySymbol(currentCurrency)}{formatBalance(calculateGlobalBalance)}
                      </span>
                    )}
                  </div>
                  
                  <IonButton
                    fill="clear"
                    onClick={toggleBalanceVisibility}
                    className="rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-all min-h-0 h-12 w-12"
                    style={{ 
                      '--padding-start': '0', 
                      '--padding-end': '0',
                      '--color': 'white',
                      '--color-hover': 'white'
                    }}
                  >
                    <IonIcon
                      icon={isBalanceHidden ? eyeOff : eye}
                      className="text-white text-xl"
                    />
                  </IonButton>
                </div>
                
                {/* Currency Selector using IonChip with Popover */}
                <IonChip 
                  outline={true} 
                  className="bg-white/20 backdrop-blur-md rounded-full px-4 py-2" 
                  onClick={openPopover}
                  style={{ 
                    '--border-color': 'white',
                    '--color': 'white',
                    border: '1px solid white',
                    borderColor: 'white !important'
                  }}
                >
                  <IonLabel className="font-medium text-sm mr-1" style={{ color: 'white' }}>
                    {currentCurrency}
                  </IonLabel>
                  <IonIcon icon={chevronDownOutline} className="text-sm" style={{ color: 'white' }}></IonIcon>
                </IonChip>
                
                <IonPopover ref={popover} isOpen={popoverOpen} onDidDismiss={() => setPopoverOpen(false)}>
                  <IonContent className="ion-padding">
                    <div className="flex flex-col gap-2">
                      {supportedCurrencies.map((currency) => (
                        <IonButton
                          key={currency.code}
                          fill="clear"
                          color="primary"
                          className="text-left justify-start w-full"
                          style={{
                            '--color': currentCurrency === currency.code 
                              ? 'var(--ion-color-primary)' 
                              : 'var(--ion-color-dark)',
                            '--color-hover': 'var(--ion-color-primary)',
                            textAlign: 'left',
                            justifyContent: 'flex-start'
                          }}
                          onClick={() => {
                            handleCurrencyChange(currency.code);
                            setPopoverOpen(false);
                          }}
                        >
                          <IonLabel style={{ textAlign: 'left', width: '100%' }}>
                            {currency.code} - {currency.name}
                          </IonLabel>
                        </IonButton>
                      ))}
                    </div>
                  </IonContent>
                </IonPopover>
              </div>
            </div>
          </div>
        </IonCardContent>
      </IonCard>
    </motion.div>
  );
}